// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import Badge, { BadgeTypeValues } from "@insite/mobius/Badge";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PreviouslyPurchasedBadge from "@werner/App/Components/CustomBadges/PreviouslyPurchasedBadge";
import ProductActions from "@werner/App/Components/Product/ProductActions";
import { isPreviouslyPurchased } from "@werner/App/Store/Selectors/ProductSelectors";
import ProductPartNumbers from "@werner/Overrides/Components/ProductPartNumbers";
import { color, ellipsis, mergeWithCss, sharedTextBadgeStyle } from "@werner/XDL/Utilities";
import React from "react";
import { css } from "styled-components";
import { hideAll } from "tippy.js";

interface Props {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    products: ProductContextModel[];
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: ProductImageStyles;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    prevPurchasedTag?: InjectableCss;
    title?: TypographyPresentationProps;
}

const containerSharedStyles = css`
    display: flex;
    align-items: center;
    gap: 14px;
    padding-bottom: 16px;
    margin-bottom: 16px;
    &:not(:last-child) {
        border-bottom: 1px solid ${color("common.ui1")};
    }
`;

const sharedTextStyle: TypographyPresentationProps = {
    size: 14,
    weight: 400,
    ellipsis: true,
    css: css`
        letter-spacing: 0;
        line-height: 16px;
    `,
};

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        css: css`
            ${containerSharedStyles}
        `,
    },
    focusedContainer: {
        css: css`
            ${containerSharedStyles}
            background-color: lightgray;
        `,
    },

    image: {
        img: {
            width: 82,
            height: 82,
            css: css`
                max-height: 100%;
            `,
        },
        linkWrappingImage: {
            css: css`
                width: 82px;
                height: 82px;
            `,
        },
    },
    infoGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ...sharedTextStyle,
            weight: 500,
        },
        css: css`
            width: calc(100% - 32px);
        `,
    },

    prevPurchasedTag: {
        ...sharedTextBadgeStyle,
    },
    title: {
        ...mergeWithCss(sharedTextStyle, {
            css: css`
                white-space: pre-wrap;
                ${ellipsis(2)};
            `,
        }),
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                {/* XNG-changes: remove title */}
                {/* <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography> */}
                {products.map(productContext => {
                    const {
                        product: { id, productNumber, manufacturerItem, productTitle },
                        productInfo: { productDetailPath },
                    } = productContext;
                    return (
                        <ProductContext.Provider value={productContext} key={id!}>
                            <StyledWrapper {...styles.container} className="autocomplete-products-container">
                                <ProductImage product={productContext} extendedStyles={styles.image} />
                                <StyledWrapper
                                    {...styles.infoGridItem}
                                    className="autocomplete-products-info-grid-item"
                                >
                                    <Link {...styles.titleLink} href={productDetailPath}>
                                        {translate("MFR #: {0}", manufacturerItem)}
                                    </Link>
                                    <Typography {...styles.title}>{productTitle}</Typography>
                                    <PreviouslyPurchasedBadge
                                        product={productContext}
                                        textBadgeWrapperCss={styles.prevPurchasedTag}
                                    />
                                    {/* XNG-Changes: add product number */}
                                    <ProductPartNumbers productNumber={productNumber} extendedStyles={{}} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </ProductContext.Provider>
                    );
                })}
            </>
        );
    }
}

export default AutocompleteProducts;
